/**
 * OAS (Old Age Security) Calculation Service
 *
 * Calculates monthly OAS benefits based on:
 * - Age (must be 65+)
 * - Years of Canadian residency (10-40)
 * - Annual income (for clawback calculation)
 *
 * ## Official Rules (2025)
 *
 * - Maximum benefit at 40+ years residency: $713.34/month
 * - Pro-rating: Benefit * (yearsInCanada / 40) for 10-39 years
 * - Minimum residency: 10 years (no benefit below this)
 * - Income clawback: 15% of income above $90,997
 * - Full clawback at: $148,451+ annual income
 *
 * ## References
 *
 * - [Government of Canada - OAS Benefits](https://www.canada.ca/en/services/benefits/publicpensions/cpp/old-age-security.html)
 * - [Service Canada - OAS Payment Amounts](https://www.canada.ca/en/services/benefits/publicpensions/cpp/old-age-security/payments.html)
 * - See docs/product/CANADIAN_RETIREMENT_RULES.md for detailed rules
 */

import BigNumber from "bignumber.js";

/**
 * OAS constants (2025 values)
 */
export const OAS_MAX_BENEFIT_2025 = 713.34; // Monthly maximum
export const OAS_MIN_AGE = 65;
export const OAS_MAX_CONTRIBUTION_YEARS = 40; // Years of residency for full benefit
export const OAS_MIN_RESIDENCY_YEARS = 10; // Minimum years to qualify
export const OAS_CLAWBACK_THRESHOLD_2025 = 90997; // Annual income threshold
export const OAS_FULL_CLAWBACK_THRESHOLD_2025 = 148451; // Full clawback threshold
export const OAS_CLAWBACK_RATE = 0.15; // 15% clawback rate

/**
 * Input parameters for OAS benefit calculation
 */
export interface OASCalculationParams {
  age: number; // Current age (must be 65+)
  yearsInCanada: number; // Years of Canadian residency after age 18 (0-40+)
  annualIncome: number; // Annual income for clawback calculation
}

/**
 * Calculate monthly OAS benefit
 *
 * @param params - Calculation parameters
 * @returns Monthly OAS benefit amount in dollars
 * @throws Error if age is below 65, yearsInCanada is negative, or annualIncome is negative
 *
 * @example
 * // Maximum benefit with full residency, low income
 * calculateOASBenefit({ age: 65, yearsInCanada: 40, annualIncome: 50000 }); // 713.34
 *
 * @example
 * // Pro-rated benefit for 20 years residency
 * calculateOASBenefit({ age: 65, yearsInCanada: 20, annualIncome: 50000 }); // 356.67
 *
 * @example
 * // With income clawback
 * calculateOASBenefit({ age: 65, yearsInCanada: 40, annualIncome: 100997 }); // 588.34
 *
 * @example
 * // Below minimum residency
 * calculateOASBenefit({ age: 65, yearsInCanada: 9, annualIncome: 50000 }); // 0
 */
export function calculateOASBenefit(params: OASCalculationParams): number {
  const { age, yearsInCanada, annualIncome } = params;

  // Validation: Age must be 65 or older
  if (age < OAS_MIN_AGE) {
    throw new Error(
      `OAS benefits can only start at age ${OAS_MIN_AGE} or later`
    );
  }

  // Validation: Years in Canada cannot be negative
  if (yearsInCanada < 0) {
    throw new Error("Years in Canada cannot be negative");
  }

  // Validation: Annual income cannot be negative
  if (annualIncome < 0) {
    throw new Error("Annual income cannot be negative");
  }

  // Step 1: Check minimum residency requirement
  if (yearsInCanada < OAS_MIN_RESIDENCY_YEARS) {
    return 0; // Not eligible
  }

  // Step 2: Calculate pro-rated base benefit
  const effectiveYears = Math.min(yearsInCanada, OAS_MAX_CONTRIBUTION_YEARS);
  const proRatingFactor = new BigNumber(effectiveYears).div(
    OAS_MAX_CONTRIBUTION_YEARS
  );
  const baseBenefit = new BigNumber(OAS_MAX_BENEFIT_2025)
    .times(proRatingFactor)
    .toNumber();

  // Step 3: Calculate income clawback
  let clawbackAmount = 0;

  if (annualIncome > OAS_CLAWBACK_THRESHOLD_2025) {
    if (annualIncome >= OAS_FULL_CLAWBACK_THRESHOLD_2025) {
      // Full clawback: benefit reduced to zero
      return 0;
    } else {
      // Partial clawback: 15% of income above threshold
      const incomeAboveThreshold = new BigNumber(annualIncome).minus(
        OAS_CLAWBACK_THRESHOLD_2025
      );
      const clawbackAnnual = incomeAboveThreshold.times(OAS_CLAWBACK_RATE);
      clawbackAmount = clawbackAnnual.div(12).toNumber(); // Convert to monthly
    }
  }

  // Step 4: Apply clawback and ensure non-negative result
  const finalBenefit = BigNumber.max(
    0,
    new BigNumber(baseBenefit).minus(clawbackAmount)
  ).toNumber();

  // Step 5: Round to 2 decimals (cents)
  return parseFloat(new BigNumber(finalBenefit).toFixed(2));
}
